import React from "react";
import Card from "components/card";
import Statistics from "./NursingStatistics";
import DollarIcon from "components/icons/WidgetIcon/DollarIcon";
import { useDealData } from "contexts/DealContext";
import { usePortfolioData } from "contexts/PortfolioContext";
import { useViewMode } from "contexts/ViewModeContext";
import { formatCurrency } from "utils/formatUtils";
import { TbTrendingUp, TbZoomMoney } from "react-icons/tb";
import { useTabs } from "contexts/TabContext";

const GeneralFinance = () => {
    const { viewMode } = useViewMode();

    // Deal data
    const {
        currentFacility: dealFacility,
        selectedPeriod: dealSelectedPeriod,
        isDealLoading,
        currentDealName
    } = useDealData();

    // Portfolio data
    const {
        currentPortfolioFacility,
        selectedPeriod: portfolioSelectedPeriod,
        isPortfolioLoading,
        currentPortfolioName
    } = usePortfolioData();

    const { addTab } = useTabs();

    // Use appropriate data based on view mode
    const currentFacility = viewMode === 'portfolio' ? currentPortfolioFacility : dealFacility;
    const selectedPeriod = viewMode === 'portfolio' ? portfolioSelectedPeriod : dealSelectedPeriod;
    const isLoading = viewMode === 'portfolio' ? isPortfolioLoading : isDealLoading;
    const currentName = viewMode === 'portfolio' ? currentPortfolioName : currentDealName;

    // Check if current facility is a group
    const isGroup = currentFacility?.details?.isGroup;

    const IconButton = ({ icon: Icon, onClick, className }) => (
        <button
            onClick={onClick}
            className={`p-1 hover:bg-gray-100 rounded-full transition-colors ${className}`}
        >
            <Icon className="w-5 h-5 text-gray-600" />
        </button>
    );

    // Check if we have the necessary data loaded
    const isDataReady = currentFacility && selectedPeriod && !isLoading;

    // Extract period data only if we have the necessary prerequisites
    const periodData = isDataReady ? currentFacility?.periods?.[selectedPeriod]?.Categories || {} : {};
    const expenseData = periodData?.Expenses || [];
    const revenueData = periodData?.Revenue || [];

    // Get financial data - using different approaches for groups vs regular facilities
    let adjustedRevenue = {};
    let adjustedExpenses = {};
    let adjustedEbitdar = 0;
    let medicaidRate = "";

    // Only process data if we have the necessary prerequisites
    if (isDataReady) {
        // If it's a group, get data directly from group details if available
        if (isGroup) {
            // Try to get pre-calculated metrics from group data
            if (currentFacility?.details) {
                // Medicaid Rate - direct from details
                if (currentFacility.details["Medicaid Rate"]) {
                    medicaidRate = currentFacility.details["Medicaid Rate"];
                }
            }

            // For financial metrics, still use the period data, but look in the pre-calculated groups data
            adjustedRevenue = revenueData.find(item => item.Type === "Adjusted Revenue") || {};
            adjustedExpenses = expenseData.find(item => item.Type === "Adjusted Expenses") || {};
            adjustedEbitdar = expenseData.find(item => item.Type === "Adjusted EBITDAR")?.Annual || 0;
        }
        // Otherwise use the standard approach for regular facilities
        else {
            adjustedRevenue = revenueData.find(item => item.Type === "Adjusted Revenue") || {};
            adjustedExpenses = expenseData.find(item => item.Type === "Adjusted Expenses") || {};
            adjustedEbitdar = expenseData.find(item => item.Type === "Adjusted EBITDAR")?.Annual || 0;

            // Standard approach for Medicaid rate
            const hudDebtCategories = currentFacility?.hudDebt?.Categories || {};
            const medicaidRateCategory = Object.entries(hudDebtCategories)
                .find(([key]) => key.startsWith("Med")) || [];
            medicaidRate = medicaidRateCategory.length > 0
                ? medicaidRateCategory[1]?.find(item =>
                    item.Type?.includes("Total Rate") ||
                    item.Type?.includes("Current") ||
                    item.Type?.includes("Med")
                )?.Amount || ""
                : "";
        }
    }

    // Function to handle icon button clicks and create tabs
    const handleIconClick = (chartType, chartTitle) => {
        // Format the tab ID from the chart type
        const tabId = chartType;

        // Create the path based on view mode
        let path;
        if (viewMode === 'portfolio') {
            if (currentName) {
                path = `/portfolio/${encodeURIComponent(currentName)}/finance-charts/${chartType}`;
            } else {
                path = `/portfolio/finance-charts/${chartType}`;
            }
        } else {
            if (currentName) {
                path = `/deals/${encodeURIComponent(currentName)}/finance-charts/${chartType}`;
            } else {
                path = `/finance-charts/${chartType}`;
            }
        }

        // Add the tab using the context
        addTab(tabId, chartTitle, path);
    };

    // Show loading state if data isn't ready
    if (!isDataReady) {
        return (
            <Card extra={"items-center justify-around w-full p-[16px] bg-cover gap-2"}>
                <h3 className="ml-6 mb-2 text-xl self-start font-bold text-gray-700 dark:text-white">
                    General Finances
                </h3>
                <div className="w-full flex justify-center items-center p-8">
                    <div className="text-gray-500">Loading financial data...</div>
                </div>
            </Card>
        );
    }

    return (
        <Card extra={"items-center justify-around w-full p-[16px] bg-cover gap-2"}>
            <h3 className="ml-6 mb-2 text-xl self-start font-bold text-gray-700 dark:text-white">
                General Finances
            </h3>

            <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="relative">
                    <div className="absolute top-2 right-2 flex gap-1">
                        <IconButton
                            icon={TbZoomMoney}
                            onClick={() => handleIconClick('revenue-details', 'Revenue Details')}
                        />
                        <IconButton
                            icon={TbTrendingUp}
                            onClick={() => handleIconClick('revenue-trends', 'Revenue Trends')}
                        />
                    </div>
                    <Statistics
                        title="Adjusted Revenue"
                        subtitle1="Annual"
                        value1={formatCurrency(adjustedRevenue?.Annual || "")}
                        subtitle2="PPD"
                        value2={formatCurrency(adjustedRevenue?.PPD || "")}
                        icon={
                            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-lightPrimary text-4xl text-brand-500 dark:!bg-navy-700 dark:text-white">
                                <DollarIcon />
                            </div>
                        }
                    />
                </div>
                <div className="relative">
                    <div className="absolute top-2 right-2 flex gap-1">
                        <IconButton
                            icon={TbZoomMoney}
                            onClick={() => handleIconClick('expense-details', 'Expense Details')}
                        />
                        <IconButton
                            icon={TbTrendingUp}
                            onClick={() => handleIconClick('expense-trends', 'Expense Trends')}
                        />
                    </div>
                    <Statistics
                        title="Adjusted Expenses"
                        subtitle1="Annual"
                        value1={formatCurrency(adjustedExpenses?.Annual) || ""}
                        subtitle2="PPD"
                        value2={formatCurrency(adjustedExpenses?.PPD) || ""}
                        icon={
                            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-lightPrimary text-xl text-brand-500 dark:!bg-navy-700 dark:text-white">
                                <DollarIcon />
                            </div>
                        }
                    />
                </div>
                <div className="relative">
                    <div className="absolute top-2 right-2 flex gap-1">
                        <IconButton
                            icon={TbZoomMoney}
                            onClick={() => handleIconClick('ebitdar-details', 'EBITDAR Details')}
                        />
                        <IconButton
                            icon={TbTrendingUp}
                            onClick={() => handleIconClick('ebitdar-trends', 'EBITDAR Trends')}
                        />
                    </div>
                    <Statistics
                        title="EBITDAR"
                        subtitle1="Adjusted"
                        value1={formatCurrency(adjustedEbitdar) || ""}
                        subtitle2="Margin"
                        value2={(() => {
                            const ppdValue = expenseData.find(item => item.Type === "Adjusted EBITDAR")?.PPD;
                            if (ppdValue === null || ppdValue === undefined || ppdValue === '') {
                                return "-";
                            }
                            const numValue = typeof ppdValue === 'number' ? ppdValue : parseFloat(ppdValue);
                            if (isNaN(numValue)) {
                                return "-";
                            }
                            return `${(numValue * 100).toFixed(0)}%`;
                        })()}
                        icon={
                            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-lightPrimary text-4xl text-brand-500 dark:!bg-navy-700 dark:text-white">
                                <DollarIcon />
                            </div>
                        }
                    />
                </div>
                <Statistics
                    title="Medicaid Rate"
                    value1={formatCurrency(medicaidRate) || ""}
                    icon={
                        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-lightPrimary text-4xl text-brand-500 dark:!bg-navy-700 dark:text-white">
                            <DollarIcon />
                        </div>
                    }
                />
            </div>
        </Card>
    );
};

export default GeneralFinance;